import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

export type Stall = components['schemas']['Stall'];
export type SlackChannel = components['schemas']['SlackChannel'];
export type SlackChannelBody = components['schemas']['SlackChannelRequest'];
export type SlackChannelPatch = components['schemas']['PatchedSlackChannelRequest'];
export type EventTypeRow = components['schemas']['EventTypeRow'];
export type SearchResults = components['schemas']['SearchResults'];
export type AttentionCounts = components['schemas']['AttentionCounts'];
export type StallAssignBody = components['schemas']['StallAssignRequest'];
export type StallSnoozeBody = components['schemas']['StallSnoozeRequest'];

/** Staff paths come from the API already resolved; the router's typed `to` needs the cast. */
export function staffPath(path: string): '/staff' {
  return path as '/staff';
}

export async function listStalls(): Promise<Stall[]> {
  return unwrap(await api.GET('/api/ops/stalls'));
}
export async function attentionCounts(): Promise<AttentionCounts> {
  return unwrap(await api.GET('/api/ops/attention'));
}
export async function assignStall(body: StallAssignBody): Promise<Stall> {
  return unwrap(await api.POST('/api/ops/stalls/assign', { body }));
}
export async function snoozeStall(body: StallSnoozeBody): Promise<Stall> {
  return unwrap(await api.POST('/api/ops/stalls/snooze', { body }));
}
export async function listSlackChannels(): Promise<SlackChannel[]> {
  return unwrap(await api.GET('/api/ops/slack-channels'));
}
export async function createSlackChannel(body: SlackChannelBody): Promise<SlackChannel> {
  return unwrap(await api.POST('/api/ops/slack-channels', { body }));
}
export async function updateSlackChannel(id: string, body: SlackChannelPatch): Promise<SlackChannel> {
  return unwrap(await api.PATCH('/api/ops/slack-channels/{public_id}', { params: { path: { public_id: id } }, body }));
}
export async function deleteSlackChannel(id: string): Promise<void> {
  unwrap(await api.DELETE('/api/ops/slack-channels/{public_id}', { params: { path: { public_id: id } } }));
}
export async function testSlackChannel(id: string): Promise<{ ok: boolean }> {
  return unwrap(await api.POST('/api/ops/slack-channels/{public_id}/test', { params: { path: { public_id: id } } }));
}
export async function listEventTypes(): Promise<EventTypeRow[]> {
  return unwrap(await api.GET('/api/ops/event-types'));
}
export async function search(q: string): Promise<SearchResults> {
  return unwrap(await api.GET('/api/search', { params: { query: { q } } }));
}

/** The stall board is a wall display: it refreshes itself every minute. */
export function useStalls() {
  return useQuery({ queryKey: ['stalls'], queryFn: listStalls, refetchInterval: 60_000 });
}

/** The staff nav's badges: open stalls and runs that need a look, refreshed with the board. */
export function useAttentionCounts() {
  return useQuery({ queryKey: ['stalls', 'attention'], queryFn: attentionCounts, refetchInterval: 60_000 });
}

/** Taking or snoozing a stall changes the board and the nav count alike. */
function useStallMutation<B>(fn: (body: B) => Promise<Stall>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stalls'] }),
  });
}

export function useAssignStall() {
  return useStallMutation(assignStall);
}

export function useSnoozeStall() {
  return useStallMutation(snoozeStall);
}

export function useSlackChannels() {
  return useQuery({ queryKey: ['slack-channels'], queryFn: listSlackChannels });
}

export function useEventTypes() {
  return useQuery({ queryKey: ['event-types'], queryFn: listEventTypes, staleTime: 3_600_000 });
}

/** Creating needs the whole channel; editing may send only what changed (and never the webhook). */
export type SaveSlackChannelArgs = { id: string; body: SlackChannelPatch } | { id?: undefined; body: SlackChannelBody };

export function useSaveSlackChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: SaveSlackChannelArgs) => {
      if (args.id !== undefined) return updateSlackChannel(args.id, args.body);
      return createSlackChannel(args.body);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['slack-channels'] }),
  });
}

export function useDeleteSlackChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSlackChannel(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['slack-channels'] }),
  });
}

export function useTestSlackChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => testSlackChannel(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['slack-channels'] }),
  });
}

export function useSearch(q: string) {
  return useQuery({ queryKey: ['search', q], queryFn: () => search(q), enabled: q.trim().length >= 2 });
}
